import {
  Container,
  Icon,
  Progress,
  Spacer,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { IUsageEvent } from '@monetize/types/app';
import { UploadIcon } from '@monetize/ui/icons';
import { RefetchOptions } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  MBox,
  MButton,
  MFlex,
  MFormField,
  MPageHeader,
  MRadio,
  MRadioGroup,
  MText,
} from '../../../components/Monetize';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { SELECTION_COL_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { DatePicker } from '../../../components/Monetize/DatePicker/DatePicker';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { LoadMore } from '../../../components/Monetize/LoadMore';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { useNonInitialEffect } from '../../../hooks/useNonInitialEffect';
import { pluralize } from '../../../utils';
import { UploadUsageModal } from '../../AccountDetails/Subscriptions/Usage/usageUpload/UploadUsageModal';
import { EditUsageEventModal } from './EditUsageEventModal';
import { UsageManagementActions } from './UsageManagementActions';
import { useTenantUsage } from './useTenantUsage';
import { VoidUsageModal } from './VoidUsageModal';

const pinnedLeftColumns = [SELECTION_COL_KEY];

export const UsageManagement = () => {
  const {
    open: isImportUsageModalOpen,
    onOpen: onImportUsageModalOpen,
    onClose: onImportUsageModalClose,
  } = useDisclosure();

  const [displayType, setDisplayType] = useState<'AGGREGATE' | 'INDIVIDUAL'>(
    'INDIVIDUAL',
  );
  const [selectedCount, setSelectedCount] = useState(0);
  const [tableInstanceKey, setTableInstanceKey] = useState(0);
  const tableRef = useRef<DataTableRef>(null);
  const [activeVoidRecords, setActiveVoidRecords] = useState<
    IUsageEvent[] | null
  >(null);
  const [activeEditRecord, setActiveEditRecord] = useState<IUsageEvent | null>(
    null,
  );

  // Bumping the instance key remounts the table, which clears its own
  // selection state; the count is the local mirror that needs resetting.
  const resetSelection = useCallback(() => {
    setSelectedCount(0);
    setTableInstanceKey((value: number) => value + 1);
  }, []);
  const handleVoidRecord = useCallback((record: IUsageEvent) => {
    setActiveVoidRecords([record]);
  }, []);

  const handleEditRecord = useCallback((record: IUsageEvent) => {
    setActiveEditRecord(record);
  }, []);

  const {
    columns,
    filters,
    formMethods,
    queryResults,
    pager,
    totalElements,
    usageRecords,
  } = useTenantUsage({
    type: displayType,
    onVoidRecord: displayType === 'INDIVIDUAL' ? handleVoidRecord : undefined,
    onEditRecord: displayType === 'INDIVIDUAL' ? handleEditRecord : undefined,
  });

  const {
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
    refetch,
  } = queryResults;

  useNonInitialEffect(() => {
    resetSelection();
  }, [filters]);

  const handleRefetch = useCallback(
    (options?: RefetchOptions) => {
      resetSelection();
      return refetch(options);
    },
    [refetch, resetSelection],
  );

  return (
    <>
      {isImportUsageModalOpen && (
        <UploadUsageModal
          onClose={() => {
            queryResults.refetch();
            onImportUsageModalClose();
          }}
        />
      )}

      {activeVoidRecords && (
        <VoidUsageModal
          usageEvents={activeVoidRecords}
          onClose={() => {
            setActiveVoidRecords(null);
            resetSelection();
            queryResults.refetch();
          }}
        />
      )}

      {activeEditRecord && (
        <EditUsageEventModal
          usageEvent={activeEditRecord}
          onClose={() => {
            setActiveEditRecord(null);
            queryResults.refetch();
          }}
        />
      )}

      <Container
        maxHeight="calc(100vh - 175px)"
        maxWidth="unset"
        px="0"
        position="relative"
        centerContent
        data-testid="usage-management-page"
      >
        <MBox minH="4px" width="100%" mt="-6" mb="6">
          {isLoading && (
            <Progress.Root size="xs" value={null} variant="neutral">
              <Progress.Track>
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
          )}
        </MBox>
        <MPageHeader
          title="Usage Management"
          subTitle={
            <MText>View and load usage across all of your accounts.</MText>
          }
        >
          <MBox>
            <MButton
              variant="primary"
              onClick={() => onImportUsageModalOpen()}
              leftIcon={
                <Icon asChild>
                  <UploadIcon />
                </Icon>
              }
              iconSpacing={4}
              type="button"
            >
              Import Usage
            </MButton>
          </MBox>
        </MPageHeader>

        <>
          <MFlex mb={2} w="100%" alignItems="center">
            <MRadioGroup
              onChange={(value) => {
                setDisplayType(value as 'INDIVIDUAL' | 'AGGREGATE');
                setSelectedCount(0);
              }}
              value={displayType}
              colorPalette="purple"
            >
              <Stack direction="row" gap={2}>
                <MRadio value="INDIVIDUAL">Individual Usage Records</MRadio>
                <MRadio value="AGGREGATE">Aggregated Usage Records</MRadio>
              </Stack>
            </MRadioGroup>
          </MFlex>
          <MFlex mb={2} w="100%" alignItems="center">
            <MBox>
              <MFormField
                error={formMethods.formState?.errors?.startTime}
                label="From"
                isHorizontal
                labelProps={{ mr: 2 }}
              >
                <Controller
                  name="startTime"
                  control={formMethods.control}
                  render={({ field }) => <DatePicker includeTime {...field} />}
                />
              </MFormField>
            </MBox>
            <MBox>
              <MFormField
                error={formMethods.formState?.errors?.endTime}
                label="to"
                labelProps={{ fontWeight: 'normal', mx: 2 }}
                isHorizontal
              >
                <Controller
                  name="endTime"
                  control={formMethods.control}
                  render={({ field }) => <DatePicker includeTime {...field} />}
                />
              </MFormField>
            </MBox>
            <UsageManagementActions
              onChangeDateRange={(startTime, endTime) => {
                formMethods.setValue('startTime', startTime);
                formMethods.setValue('endTime', endTime);
              }}
            />
            <RefreshTableButton refetch={handleRefetch} />
            <Spacer />
            {displayType === 'INDIVIDUAL' && selectedCount > 0 && (
              <MButton
                variant="secondary"
                mr={2}
                onClick={() =>
                  setActiveVoidRecords(
                    tableRef.current?.getSelectedRows<IUsageEvent>() ?? [],
                  )
                }
                type="button"
              >
                Void {selectedCount} {pluralize('Record', selectedCount)}
              </MButton>
            )}
            {!!usageRecords.length && (
              <ExportEntityButton
                entity={
                  displayType === 'INDIVIDUAL' ? 'usage' : 'usage-aggregate'
                }
                applyInternalFiltersWithoutConfirmation
                internalFilters={filters}
                sortField={pager.sortField}
                sortOrder={pager.sortOrder}
                isDisabled={isRefetching}
                getFilename={() => `usage-export-${Date.now()}.csv`}
              />
            )}
          </MFlex>

          <DataTable
            ref={tableRef}
            enablePagination={false}
            key={`${displayType}-${tableInstanceKey}`}
            columns={columns}
            data={usageRecords}
            enableVirtualization
            clientControlled
            isLoading={isLoading}
            pinnedLeftColumns={
              displayType === 'INDIVIDUAL' ? pinnedLeftColumns : undefined
            }
            onSelectedRowCountChange={
              displayType === 'INDIVIDUAL' ? setSelectedCount : undefined
            }
          />
          {!!usageRecords.length && (
            <MFlex flexDirection="column" mb={4} minH="70px">
              <LoadMore
                fetchedElementLength={usageRecords.length}
                totalElements={totalElements}
                isLoading={isFetchingNextPage}
                onLoadMore={fetchNextPage}
              />
            </MFlex>
          )}
        </>
      </Container>
    </>
  );
};
